/**
 * Checkout rules set by the owner (Settings > Checkout rules): what cashiers may do alone and what needs a
 * supervisor's approval on the till (staff card + PIN, fingerprint or email + PIN).
 */
/** Payment methods the owner can offer or hide at checkout. */
export const PAYMENT_METHOD_OPTIONS = [
    { key: 'cash', label: 'Cash' },
    { key: 'mpesa', label: 'M-Pesa', hint: 'Prompt on the phone, Paybill/Till, or a receipt code' },
    { key: 'card', label: 'Card', hint: 'Bank card terminal or card machine' },
    { key: 'gateway', label: 'Mobile money & online cards', hint: 'Payment gateways (Pesapal, Paystack...)' },
    { key: 'bank', label: 'Bank transfer' },
    { key: 'credit', label: 'On account (customer credit)' },
    { key: 'loyalty', label: 'Loyalty points' },
    { key: 'voucher', label: 'Gift voucher' },
];
/** Pages an owner can allow or hide for cashiers (their permissions already exclude everything else). */
export const CASHIER_PAGE_OPTIONS = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'sales', label: 'Sales history' },
    { key: 'returns', label: 'Returns' },
    { key: 'shifts', label: 'Shifts & cash' },
    { key: 'products', label: 'Products (view)' },
    { key: 'stock', label: 'Stock levels (view)' },
    { key: 'customers', label: 'Customers (view)' },
    { key: 'onlineOrders', label: 'Online orders' },
    { key: 'support', label: 'Support' },
];
export const CHECKOUT_RULE_DEFAULTS = {
    items: { removeNeedsApproval: false, reduceQtyNeedsApproval: false, clearNeedsApproval: false, holdAllowed: true, maxQtyPerLine: 0 },
    discounts: { allowed: true, lineLimitBp: 1000, saleLimitBp: 1000, maxAmountCents: 0 },
    shifts: { required: true, minOpeningFloatCents: 0, blindClose: false, closeNeedsApproval: false, maxCashInDrawerCents: 0, oneOpenShiftPerCashier: true },
    mpesa: { watchTillPayments: true, autoComplete: true },
    pricing: { wholesaleAtTill: false, wholesaleNeedsApproval: false, wholesaleByQuantity: true },
    kra: { waitSeconds: 30, printUnsigned: true },
    security: { lockAfterMinutes: 0 },
    transfers: { approvalRequired: false },
    customer: { phone: 'both', createFromPhone: true, awardLoyalty: true, required: false },
    cashierPages: CASHIER_PAGE_OPTIONS.map((p) => p.key),
    paymentMethods: PAYMENT_METHOD_OPTIONS.map((p) => p.key),
};
const bool = (v, d) => (typeof v === 'boolean' ? v : d);
const int = (v, d, max = Number.MAX_SAFE_INTEGER) => (typeof v === 'number' && Number.isInteger(v) && v >= 0 ? Math.min(v, max) : d);
/** Saved rules over the defaults. Older settings (requireOpenShift, cashierDiscountLimitBp) fill in when unset. */
export function mergeCheckoutRules(saved, legacy = {}) {
    const s = (saved && typeof saved === 'object' ? saved : {});
    const d = CHECKOUT_RULE_DEFAULTS;
    const legacyLimit = typeof legacy.cashierDiscountLimitBp === 'number' ? legacy.cashierDiscountLimitBp : d.discounts.saleLimitBp;
    return {
        items: {
            removeNeedsApproval: bool(s.items?.removeNeedsApproval, d.items.removeNeedsApproval),
            reduceQtyNeedsApproval: bool(s.items?.reduceQtyNeedsApproval, d.items.reduceQtyNeedsApproval),
            clearNeedsApproval: bool(s.items?.clearNeedsApproval, d.items.clearNeedsApproval),
            holdAllowed: bool(s.items?.holdAllowed, d.items.holdAllowed),
            maxQtyPerLine: int(s.items?.maxQtyPerLine, d.items.maxQtyPerLine, 100000),
        },
        discounts: {
            allowed: bool(s.discounts?.allowed, d.discounts.allowed),
            lineLimitBp: int(s.discounts?.lineLimitBp, legacyLimit, 10000),
            saleLimitBp: int(s.discounts?.saleLimitBp, legacyLimit, 10000),
            maxAmountCents: int(s.discounts?.maxAmountCents, d.discounts.maxAmountCents),
        },
        shifts: {
            required: bool(s.shifts?.required, typeof legacy.requireOpenShift === 'boolean' ? legacy.requireOpenShift : d.shifts.required),
            minOpeningFloatCents: int(s.shifts?.minOpeningFloatCents, d.shifts.minOpeningFloatCents),
            blindClose: bool(s.shifts?.blindClose, d.shifts.blindClose),
            closeNeedsApproval: bool(s.shifts?.closeNeedsApproval, d.shifts.closeNeedsApproval),
            maxCashInDrawerCents: int(s.shifts?.maxCashInDrawerCents, d.shifts.maxCashInDrawerCents),
            oneOpenShiftPerCashier: bool(s.shifts?.oneOpenShiftPerCashier, d.shifts.oneOpenShiftPerCashier),
        },
        mpesa: {
            watchTillPayments: bool(s.mpesa?.watchTillPayments, d.mpesa.watchTillPayments),
            autoComplete: bool(s.mpesa?.autoComplete, d.mpesa.autoComplete),
        },
        pricing: {
            wholesaleAtTill: bool(s.pricing?.wholesaleAtTill, d.pricing.wholesaleAtTill),
            wholesaleNeedsApproval: bool(s.pricing?.wholesaleNeedsApproval, d.pricing.wholesaleNeedsApproval),
            wholesaleByQuantity: bool(s.pricing?.wholesaleByQuantity, d.pricing.wholesaleByQuantity),
        },
        security: { lockAfterMinutes: int(s.security?.lockAfterMinutes, d.security.lockAfterMinutes, 240) },
        transfers: { approvalRequired: bool(s.transfers?.approvalRequired, d.transfers.approvalRequired) },
        kra: {
            waitSeconds: int(s.kra?.waitSeconds, d.kra.waitSeconds, 120),
            printUnsigned: bool(s.kra?.printUnsigned, d.kra.printUnsigned),
        },
        customer: {
            phone: ['off', 'ask', 'mpesa', 'both'].includes(s.customer?.phone) ? s.customer.phone : d.customer.phone,
            createFromPhone: bool(s.customer?.createFromPhone, d.customer.createFromPhone),
            awardLoyalty: bool(s.customer?.awardLoyalty, d.customer.awardLoyalty),
            required: bool(s.customer?.required, d.customer.required),
        },
        cashierPages: Array.isArray(s.cashierPages) ? s.cashierPages.filter((k) => CASHIER_PAGE_OPTIONS.some((p) => p.key === k)) : d.cashierPages,
        paymentMethods: paymentMethodsOf(s.paymentMethods),
    };
}
/** True for a user whose only built-in role is Cashier (the page list applies to them). */
export function isPlainCashier(roles) {
    return roles.length > 0 && roles.every((r) => r === 'cashier');
}
/** Saved payment methods; nothing valid saved means all of them (a till always needs a way to be paid). */
function paymentMethodsOf(v) {
    const list = Array.isArray(v) ? PAYMENT_METHOD_OPTIONS.map((o) => o.key).filter((k) => v.includes(k)) : [];
    return list.length ? list : CHECKOUT_RULE_DEFAULTS.paymentMethods;
}
/** True when the method may be used at checkout. Methods not in the list of options (e.g. "other") are not limited. */
export function paymentMethodAllowed(rules, method) {
    return !PAYMENT_METHOD_OPTIONS.some((o) => o.key === method) || rules.paymentMethods.includes(method);
}
//# sourceMappingURL=checkoutRules.js.map